import { Tooltip } from 'antd';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Form } from 'components/atoms/form';
import type { Frequency } from 'data/templates/status-updates/type';
import { useTranslation } from 'hooks/use-translation';
import { useAccountProfile } from 'queries/account';
import { generateArr } from 'utils/time-dates';
import { getTimezoneAbbreviation } from 'utils/timezone';
import { getNextSendTime } from '../../asu-utils';
import { useStatusUpdatesTemplatesContext } from '../../context';
import {
  ConfigTitle,
  FreqSelectorWrapper,
  NextUpdate,
  NumberSelect,
  PublishDescription,
  WeekLabel,
  WeekSelect,
} from '../styles';
import { TimeSelector } from './time-selector';
import { WeekDaySelector } from './week-day-selector';

const TimezoneInfo = styled.label`
  margin-left: var(--spacing-02);
  display: inline-flex;
  align-items: center;
`;

interface Props {
  frequency: Frequency;
  onFrequencyUpdate: (obj: Partial<Frequency>) => void;
}

const numberOptions = generateArr(4);

export const FrequencySelector = ({
  frequency,
  onFrequencyUpdate,
}: Props): React.ReactElement => {
  const { data: account } = useAccountProfile();
  const weeks = useTranslation(
    'templates.status_updates.tab.config.freq.weeks',
  );

  const { serverPublishedTime, setServerPublishedTime } =
    useStatusUpdatesTemplatesContext();

  const onWeekDaySelector = (val) => {
    // On user action, reset server published time
    if (serverPublishedTime) {
      setServerPublishedTime(null);
    }
    onFrequencyUpdate({
      onDay: {
        label: val,
        value: val,
      },
    });
  };

  const onIntervalChange = (selected) =>
    onFrequencyUpdate({
      interval: selected,
    });

  const onTimeSelector = (val) => {
    if (serverPublishedTime) {
      setServerPublishedTime(null);
    }
    onFrequencyUpdate({
      onTime: val,
    });
  };

  const frequencyLabel = useTranslation(
    'templates.status_updates.tab.config.freq.title',
  );

  const nextScheduledDate = getNextSendTime(
    frequency.onTime.value,
    frequency.onDay.value,
    account?.timeZone,
  );
  const { isPrivate, isPublished } = useStatusUpdatesTemplatesContext();

  return (
    <FreqSelectorWrapper>
      <ConfigTitle>
        <FormattedMessage id="templates.status_updates.tab.config.schedule" />
      </ConfigTitle>
      <PublishDescription>
        <FormattedMessage id="templates.status_updates.tab.config.publish.description" />
      </PublishDescription>
      <Form.Item label={frequencyLabel}>
        <WeekSelect>
          <NumberSelect
            options={numberOptions}
            value={frequency.interval}
            onChange={onIntervalChange}
          />
          <WeekLabel>{weeks}</WeekLabel>
        </WeekSelect>
        <WeekDaySelector
          onChange={onWeekDaySelector}
          weekDay={frequency.onDay}
        />
        <TimeSelector onChange={onTimeSelector} value={frequency.onTime} />
      </Form.Item>
      {isPublished ? (
        <NextUpdate data-cy="templates.autopublish.schedule.timestamp">
          <FormattedMessage
            id="templates.status_updates.tab.config.scheduled_date"
            values={{
              private: isPrivate,
              date: serverPublishedTime || nextScheduledDate,
              strong: (chunks) => (
                <Tooltip
                  title={
                    account?.timeZone ? (
                      <FormattedMessage
                        id="timezone.info.tooltip"
                        values={{
                          timezone: account?.timeZone,
                        }}
                      />
                    ) : null
                  }
                >
                  <strong>
                    {chunks}
                    {account?.timeZone ? (
                      <TimezoneInfo>
                        {getTimezoneAbbreviation(account?.timeZone)}
                      </TimezoneInfo>
                    ) : null}
                  </strong>
                </Tooltip>
              ),
            }}
          />
        </NextUpdate>
      ) : null}
    </FreqSelectorWrapper>
  );
};
